import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ProgressChart } from '@uplift/ui-v2';
import { useAssessmentsKeysQuery, useAssessmentSubmissionsQuery, useFormDefinitionQuery, useUserQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { AssessmentKeys, getAssessmentFormMaxScore } from '@uplift/types';


const useStyles = makeStyles()(theme => ({
  chartItemColumn: {
    padding: theme.spacing(0, 2),
  }
}));

type ProgressChartWithDetailsProps = {
  patientId: string;
  assessmentId: string;
  assessmentKey: AssessmentKeys;
  onAssessmentDetails?: (assessment: { id: string; key: AssessmentKeys }) => void;
};
const ProgressChartWithDetails: React.VFC<ProgressChartWithDetailsProps> = ({
  patientId,
  assessmentId,
  assessmentKey,
  onAssessmentDetails,
}) => {
  const { classes } = useStyles();
  const { data: formDefinition } = useFormDefinitionQuery(assessmentId, { suspense: true });
  const { data: submissions } = useAssessmentSubmissionsQuery(patientId, assessmentKey, { suspense: true });
  const { data: assessmentKeys } = useAssessmentsKeysQuery({ suspense: true });
  const { data: user } = useUserQuery(patientId);

  const maxScore = useMemo(() => getAssessmentFormMaxScore(formDefinition?.formId, assessmentKeys), [ assessmentKeys, formDefinition?.formId ]);

  const chartItems = React.useMemo(() => {
    const completedSubmissions = submissions.filter(s => s.submission.completedAt);

    return completedSubmissions.map(submission => ({
      datetime: CalendarDateService.date(submission.submission.createdAt),
      score: submission.score,
      scoreDiffPercentage: submission.scoreDiffPercentage,
    }));
  }, [ submissions ]);
  const newestSubmission = React.useMemo(() => chartItems?.[0], [ chartItems ]);

  const customTitle = useMemo(() => {
    if (user?.status === 'initial' || newestSubmission === undefined) {
      return <Typography component="i" color="textSecondary">None</Typography>;
    }
    return undefined;
  }, [ newestSubmission, user?.status ]);

  const isDisabled = useMemo(() => {
    if (user?.status === 'initial' || newestSubmission === undefined) {
      return true;
    }
    return undefined;
  }, [ newestSubmission, user?.status ]);

  if (!submissions?.length) {
    return null;
  }

  return (
    <Box key={assessmentKey} className={classes.chartItemColumn}>
      <ProgressChart
        title={formDefinition.name}
        data={chartItems}
        isDisabled={isDisabled}
        maxScore={maxScore}
        customTitleValue={customTitle}
        summary={newestSubmission}
        fillUpTo={CalendarDateService.date()}
        onDetails={onAssessmentDetails && (() => {
          onAssessmentDetails({ id: assessmentId, key: assessmentKey });
        })}
      />
    </Box>
  );
};

export type AssessmentsProps = {
  patientId: string;
  onAssessmentDetails: ProgressChartWithDetailsProps['onAssessmentDetails'];
};

export const Assessments: React.VFC<AssessmentsProps> = ({ patientId, onAssessmentDetails }) => {
  const { data } = useAssessmentsKeysQuery({ suspense: true });

  return (
    <>
      {(Object.keys(data ?? {}) as (keyof typeof data)[]).map(assessmentKey => (
        <ProgressChartWithDetails
          key={assessmentKey}
          patientId={patientId}
          assessmentId={data[assessmentKey]}
          assessmentKey={assessmentKey}
          onAssessmentDetails={onAssessmentDetails}
        />
      ))}
    </>
  );
};
